import type { Services } from '../contracts';
import type {
  Address, AffiliateSummary, Banner, CartLine, Category, Coupon, LoyaltyAccount, LoyaltyTransaction,
  Order, Page, PlaceOrderInput, Product, ProductQuery, Review, Tenant, Testimonial, User, Wallet, WalletTransaction,
} from '@/types';
import { api, qs, tokenStore } from './client';

type AuthPayload = { token: string; user: User };

export const httpServices: Services = {
  tenant: {
    getCurrent: () => api<Tenant>('/api/tenant'),
  },
  catalog: {
    listCategories: () => api<Category[]>('/api/catalog/categories'),
    getCategory: (slug) => api<Category>(`/api/catalog/categories/${encodeURIComponent(slug)}`),
    listProducts: (query?: ProductQuery) => api<Page<Product>>(`/api/catalog/products${qs({ ...query })}`),
    getProduct: (slug) => api<Product>(`/api/catalog/products/${encodeURIComponent(slug)}`),
    getProductsByIds: (ids) => api<Product[]>('/api/catalog/products/by-ids', { method: 'POST', body: JSON.stringify({ ids }) }),
    getRelated: (productId, limit = 8) => api<Product[]>(`/api/catalog/products/${productId}/related${qs({ limit })}`),
    listReviews: (productId) => api<Review[]>(`/api/catalog/products/${productId}/reviews`),
    suggest: (q) => api<Product[]>(`/api/catalog/suggest${qs({ q })}`),
  },
  marketing: {
    listBanners: () => api<Banner[]>('/api/marketing/banners'),
    listTestimonials: () => api<Testimonial[]>('/api/marketing/testimonials'),
    submitContact: (input) => api<{ ticketId: string }>('/api/marketing/contact', { method: 'POST', body: JSON.stringify(input) }),
  },
  coupons: {
    listPublic: () => api<Coupon[]>('/api/coupons'),
    listMine: () => api<{ available: Coupon[]; inactive: { coupon: Coupon; reason: string }[] }>('/api/coupons/mine'),
    validate: (code, lines: CartLine[]) => api('/api/coupons/validate', { method: 'POST', body: JSON.stringify({ code, lines }) }),
  },
  auth: {
    currentUser: async () => {
      if (!tokenStore.read()) return null;
      try { return await api<User>('/api/auth/me'); }
      catch { tokenStore.write(null); return null; }
    },
    login: async (input) => {
      const res = await api<AuthPayload>('/api/auth/login', { method: 'POST', body: JSON.stringify(input) });
      tokenStore.write(res.token);
      return res.user;
    },
    register: async (input) => {
      const res = await api<AuthPayload>('/api/auth/register', { method: 'POST', body: JSON.stringify(input) });
      tokenStore.write(res.token);
      return res.user;
    },
    logout: async () => {
      try { await api('/api/auth/logout', { method: 'POST' }); } catch { /* ignore */ }
      tokenStore.write(null);
    },
    updateProfile: async (input) => {
      const res = await api<AuthPayload>('/api/auth/profile', { method: 'PATCH', body: JSON.stringify(input) });
      tokenStore.write(res.token);
      return res.user;
    },
    listAddresses: () => api<Address[]>('/api/auth/addresses'),
    saveAddress: (a) => api<Address>('/api/auth/addresses', { method: 'PUT', body: JSON.stringify(a) }),
    removeAddress: (id) => api<void>(`/api/auth/addresses/${id}`, { method: 'DELETE' }),
  },
  orders: {
    list: () => api<Order[]>('/api/orders'),
    get: (id) => api<Order>(`/api/orders/${id}`),
    place: (input: PlaceOrderInput) => api<Order>('/api/orders', { method: 'POST', body: JSON.stringify(input) }),
    placeGuest: (input) => api<Order>('/api/orders/guest', { method: 'POST', body: JSON.stringify(input) }),
    cancel: (id) => api<Order>(`/api/orders/${id}/cancel`, { method: 'POST' }),
    requestReturn: (id, reason) => api<void>(`/api/orders/${id}/return`, { method: 'POST', body: JSON.stringify({ reason }) }),
  },
  wallet: {
    get: () => api<Wallet>('/api/wallet'),
    transactions: () => api<WalletTransaction[]>('/api/wallet/transactions'),
    topUp: (amount) => api<Wallet>('/api/wallet/topup', { method: 'POST', body: JSON.stringify({ amount }) }),
  },
  loyalty: {
    get: () => api<{ account: LoyaltyAccount; transactions: LoyaltyTransaction[] }>('/api/loyalty'),
    redeemReward: (rewardId) => api<LoyaltyAccount>('/api/loyalty/redeem', { method: 'POST', body: JSON.stringify({ rewardId }) }),
  },
    affiliate: {
    summary: () => api<AffiliateSummary>('/api/affiliate'),
  },
  wishlist: {
    list: () => api<Product[]>('/api/wishlist'),
    listIds: () => api<string[]>('/api/wishlist/ids'),
    toggle: (productId: string) => api<{ inWishlist: boolean; productId: string }>(`/api/wishlist/${productId}/toggle`, { method: 'POST' }),
  },
};
